/**
 * Les lectures de « Ma journée » — celles de l'ancien `TutorHomeTabScreen.js`, et seulement elles.
 *
 * - Au montage : « Mon score » (`getAllScores` : l'ancien `useTutorScore(id, false, "ALL")` relisait
 *   à chaque montage de l'accueil).
 * - Au retour sur l'accueil (le préparer, un compte-rendu) : les tâches (`getTutorTasks`).
 * - « Rafraîchir » : le compte, les compteurs d'offres, le score, les tâches.
 *
 * Un geste refusé de l'accueil (une lecture de « Rafraîchir », « Mes paiements », le formulaire Stripe
 * de « À faire ») se dit ici, une fois par geste : `notice`, le `<Snackbar>` de la page, avec le
 * message du serveur. La boîte « Erreur » que la coquille legacy ouvrait sur le compte, et celles du
 * réseau, se taisent sous l'espace tuteur.
 * Le compte, les compteurs et les tâches sont aussi relus par la coquille legacy à son montage et à sa
 * prise de focus (`BottomTabsNavigator`).
 *
 * Aucune liste d'offres n'est lancée d'ici : chaque lecture d'une liste d'offres marque le masteur
 * « actif » (`last_proposals_viewed_at`, qui décide de la désactivation à 90 jours). L'offre de
 * l'accueil se lit sur les listes que l'onglet « Offres de cours » a déjà rangées dans le compte.
 */
import { useIsFocused } from "@react-navigation/native"
import { useCallback, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { authService } from "../../../../../../data/services/authService"
import { groupAttributionService } from "../../../../../../data/services/groupAttributionService"
import { tutorMonthlyScoreService } from "../../../../../../data/services/tutorMonthlyScoreService"
import { tutorsService } from "../../../../../../data/services/tutorsService"
import { useGestureNotice } from "../../../hooks/useGestureNotice"

/** Ce que dit un geste refusé sans message du serveur : en pratique le réseau (la connexion coupée, un serveur injoignable). */
export const TUTOR_HOME_GESTURE_FAILED = "Ça n'a pas pu se faire. Vérifie ta connexion puis réessaie."

export function useTutorHomeData() {
    const dispatch = useDispatch()
    const isFocused = useIsFocused()
    const firstFocusRef = useRef(true)

    const fetchTasks = useCallback(() => {
        dispatch(apiRequest(tutorsService.getTutorTasks, {}, {}, {}) as never)
    }, [dispatch])

    useEffect(() => {
        dispatch(apiRequest(tutorMonthlyScoreService.getAllScores) as never)
    }, [dispatch])

    useEffect(() => {
        if (!isFocused) return
        // Le premier focus est le montage : la coquille vient de lire les tâches.
        if (firstFocusRef.current) {
            firstFocusRef.current = false
            return
        }
        fetchTasks()
    }, [isFocused, fetchTasks])

    // Un geste refusé de l'accueil se dit dans son `<Snackbar>` : le premier refus d'un rafraîchissement,
    // les gestes Stripe (`tell`, passé à `useTutorHomeState`).
    const {
        notice,
        tell: tellFailure,
        dismiss: dismissNotice,
        readOptions,
    } = useGestureNotice(TUTOR_HOME_GESTURE_FAILED)
    const refresh = useCallback(() => {
        const options = readOptions()
        for (const read of [
            authService.getUser,
            groupAttributionService.getProposalsCount,
            tutorMonthlyScoreService.getAllScores,
            tutorsService.getTutorTasks,
        ]) {
            dispatch(apiRequest(read, {}, {}, {}, null, null, options) as never)
        }
    }, [dispatch, readOptions])

    // Une relecture de l'accueil en vol : le bouton qui rafraîchit tourne. Lu à part du view-model, pour
    // que le début et la fin d'une relecture ne recalculent pas toute la page.
    const refreshing = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) =>
            !!(state?.api?.getUser?.request || state?.api?.getAllScores?.request || state?.api?.getTutorTasks?.request)
    )

    return { refresh, refreshing, notice, dismissNotice, tellFailure }
}
